import React, { createContext, useState, useContext, useEffect } from 'react';
import {
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  signOut,
  onAuthStateChanged,
  sendPasswordResetEmail,
  GoogleAuthProvider,
  signInWithPopup,
  updateProfile,
} from 'firebase/auth';
import { auth, db } from '../firebase';
import { doc, getDoc, setDoc } from 'firebase/firestore';

const AuthContext = createContext();

export const useAuth = () => {
  return useContext(AuthContext);
};

export const AuthProvider = ({ children }) => {
  const [currentUser, setCurrentUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const createUserProfile = async (user) => {
    try {
      const userRef = doc(db, 'users', user.uid);
      const userSnap = await getDoc(userRef);
      
      // Get username from email (everything before @)
      const username = user.email.split('@')[0];
      
      if (!userSnap.exists()) {
        await setDoc(userRef, {
          email: user.email,
          displayName: username,
          createdAt: new Date().toISOString(),
          lastLogin: new Date().toISOString(),
        });

        // Update the user's display name in Firebase Auth
        await updateProfile(user, {
          displayName: username
        });
      } else {
        await setDoc(userRef, {
          lastLogin: new Date().toISOString()
        }, { merge: true });
      }
    } catch (error) {
      console.error('Error creating user profile:', error);
      throw new Error('Failed to create user profile: ' + error.message);
    }
  };

  // Update user profile
  const updateUserProfile = async (profileData) => {
    try {
      setError(null);
      if (!auth.currentUser) throw new Error('No user logged in');
      
      // Update Firebase Auth profile
      await updateProfile(auth.currentUser, profileData);
      
      // Update Firestore user document
      const userRef = doc(db, 'users', auth.currentUser.uid);
      await setDoc(userRef, {
        ...profileData,
        updatedAt: new Date().toISOString()
      }, { merge: true });

      // Update current user state
      setCurrentUser({ ...auth.currentUser });
    } catch (error) {
      console.error('Update Profile Error:', error);
      setError(error.message);
      throw error;
    }
  };

  // Sign up function
  const signup = async (email, password) => {
    try {
      setError(null);
      const userCredential = await createUserWithEmailAndPassword(auth, email, password);
      await createUserProfile(userCredential.user);
      return userCredential.user;
    } catch (error) {
      console.error('Signup Error:', error);
      setError(error.message);
      throw error;
    }
  };

  // Sign in function
  const login = async (email, password) => {
    try {
      setError(null);
      const userCredential = await signInWithEmailAndPassword(auth, email, password);
      await createUserProfile(userCredential.user);
      return userCredential.user;
    } catch (error) {
      console.error('Login Error:', error);
      setError(error.message);
      throw error;
    }
  };

  // Google sign in
  const signInWithGoogle = async () => {
    try {
      setError(null);
      const provider = new GoogleAuthProvider();
      const result = await signInWithPopup(auth, provider);
      await createUserProfile(result.user);
      return result.user;
    } catch (error) {
      console.error('Google Sign In Error:', error);
      setError(error.message);
      throw error;
    }
  };

  // Logout function
  const logout = async () => {
    try {
      setError(null);
      await signOut(auth);
    } catch (error) {
      console.error('Logout Error:', error);
      setError(error.message);
      throw error;
    }
  };

  // Password reset
  const resetPassword = async (email) => {
    try {
      setError(null);
      await sendPasswordResetEmail(auth, email);
    } catch (error) {
      console.error('Password Reset Error:', error);
      setError(error.message);
      throw error;
    }
  };

  // Listen to auth state changes
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      setCurrentUser(user);
      setLoading(false);
    });

    return unsubscribe;
  }, []);

  const value = {
    currentUser,
    error,
    signup,
    login,
    logout,
    resetPassword,
    signInWithGoogle,
    updateUserProfile,
  };

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
};

export default AuthContext; 